import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ParkerService } from '../../api/gen/api/parker.service';
import { ParkerLicenseDto } from '../../api/gen/model/parkerLicenseDto';
import { LicensePlateDto } from '../../api/gen/model/licensePlateDto';
import { AddMyLicensePlateRequest } from '../../api/gen/model/addMyLicensePlateRequest';
import { LicensePlatePolicyDto } from '../../api/gen/model/licensePlatePolicyDto';

/**
 * Thin wrapper over the generated {@link ParkerService} for the *authenticated* parker's own plates.
 * All generated-client coupling for "my license plates" lives here — so after
 * `npm run generate:api:parker`, if the generator names methods/models differently this is the
 * only file to reconcile.
 *
 * Backend: ParkerController `me/license-plates`(+`-policy`) endpoints —
 *   GET    /parker/me/license-plates                  -> parkerMeLicensePlatesGet()
 *   POST   /parker/me/license-plates                  -> parkerMeLicensePlatesPost(body)
 *   DELETE /parker/me/license-plates/{licensePlateId} -> parkerMeLicensePlatesLicensePlateIdDelete(id)
 *   GET    /parker/me/license-plate-policy             -> parkerMeLicensePlatePolicyGet(parkingSpaceId?)
 */
@Injectable({ providedIn: 'root' })
export class MyLicensePlatesService {
  private readonly parker = inject(ParkerService);

  /** Plates registered on the current parker's account. */
  list(): Observable<ParkerLicenseDto[]> {
    return this.parker.parkerMeLicensePlatesGet();
  }

  /** Registers (create-or-reuse) a plate onto the current parker's account. Idempotent. */
  add(request: AddMyLicensePlateRequest): Observable<LicensePlateDto> {
    return this.parker.parkerMeLicensePlatesPost(request);
  }

  /** Unlinks a plate from the current parker's account. */
  remove(licensePlateId: string): Observable<string> {
    return this.parker.parkerMeLicensePlatesLicensePlateIdDelete(licensePlateId);
  }

  /**
   * The self-service plate cap and change cooldown in force. Omit `parkingSpaceId` to fall back to
   * the configured JKU space, matching {@link add}'s own default.
   */
  getPolicy(parkingSpaceId?: string): Observable<LicensePlatePolicyDto> {
    return this.parker.parkerMeLicensePlatePolicyGet(parkingSpaceId);
  }
}
